import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import {
  AsyncState,
  BookingSummary,
  PageLoading,
  StickyActionFooter
} from '@link/share/rail-booking';

import { useStore } from '@link/emot/stores';
import { getSafeReturnUrl, readOrderFlowContext } from '@link/emot/utils';

import styles from './submitted.module.css';

const SubmittedView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useBlocker(
    ({ historyAction }) =>
      location.state?.submittedOrder === true && historyAction === 'POP'
  );
  const { token, bookingId: routeBookingId } = useParams();
  const { emot } = useStore();
  const savedContext = useMemo(
    () => readOrderFlowContext(token, routeBookingId),
    [routeBookingId, token]
  );
  const isChange = (savedContext?.orderType ?? emot.orderType) === 'change';
  const { bookingInfo } = emot;
  const tUrl = savedContext?.tUrl ?? emot.tUrl;
  const bookingId = routeBookingId || emot.bookingId || bookingInfo?.bookingId;
  const [loading, setLoading] = useState(Boolean(bookingId));
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (token) emot.setToken(token);
    if (routeBookingId) emot.setBookingId(routeBookingId);
    if (savedContext) {
      emot.setOrderType(savedContext.orderType);
      emot.setTUrl(savedContext.tUrl);
    }
    if (!bookingId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadFailed(false);
    void emot
      .getBookingData(bookingId)
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [bookingId, emot, routeBookingId, savedContext, token]);

  const handleBackHome = () => {
    const returnUrl = getSafeReturnUrl(tUrl);
    if (returnUrl) {
      window.location.replace(returnUrl);
    } else {
      navigate('/');
    }
  };

  if (loading) return <PageLoading />;

  if (loadFailed || !bookingInfo) {
    return (
      <AsyncState
        status="error"
        title={loadFailed ? t('common.loadFailed') : t('common.noBooking')}
        actionLabel={t('views.submitted.backHome')}
        onAction={handleBackHome}
      />
    );
  }

  const quantities =
    bookingInfo.units?.map(
      unit => `${unit.name ?? ''} ×${unit.quantity ?? 0}`
    ) ?? [];
  const trainType = bookingInfo.trainMode
    ? `${bookingInfo.trainMode}${
        bookingInfo.trainName ? `（${bookingInfo.trainName}）` : ''
      }`
    : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.intro}>
          <h1 className={styles.title}>
            {t(
              isChange ? 'views.change.submittedTitle' : 'views.submitted.title'
            )}
          </h1>
          <p className={styles.thanks}>
            {t(
              isChange
                ? 'views.change.submittedThanks'
                : 'views.submitted.thanks'
            )}
          </p>
        </div>

        <BookingSummary
          data={{
            ticketName: t('views.submitted.ticketName'),
            departTime: bookingInfo.departTime,
            arriveTime: bookingInfo.arriveTime,
            duration: bookingInfo.timeDuration,
            trainName: bookingInfo.trainName,
            fromStation: bookingInfo.fromStationName,
            toStation: bookingInfo.toStationName,
            travelDate: bookingInfo.departDate,
            quantities,
            trainType,
            seatClass: bookingInfo.seatClassName,
            seatPreference: bookingInfo.seatPreference?.name
          }}
          labels={{
            route: t('views.submitted.route'),
            travelDate: t('views.submitted.travelDate'),
            quantities: t('views.submitted.ticketCount'),
            trainType: t('views.submitted.trainType'),
            seatClass: t('views.submitted.seatClass'),
            seatPreference: t('views.submitted.preference')
          }}
        />
      </div>

      <StickyActionFooter
        label={t('views.submitted.backHome')}
        onClick={handleBackHome}
      />
    </div>
  );
};

const Submitted = observer(SubmittedView);

export default Submitted;
